import {
  createMailtoUrl,
  ONBOARDING_EMAIL_TEMPLATES,
} from 'app/pages/teams/settings/onboarding/constants/emailTemplates'
import { ONBOARDING_SECTIONS } from 'app/pages/teams/settings/onboarding/constants/sections'
import { ONBOARDING_STEP_IDS } from 'app/pages/teams/settings/onboarding/constants/stepIds'
import * as snugNotifier from 'app/services/snugNotifier'
import { urlIds, urlTo } from 'app/sm/helpers'
import { api } from 'app/utils/api/helpers'
import * as widgetHelpers from 'app/utils/widgets/helpers'

// TypeScript interfaces for onboarding API
export interface OnboardingStep {
  id: string
  title: string
  description: string
  actionText: string
  buttonOnClickHandler: () => void
  showVideo: boolean
  videoUrl?: string
  section: string
  completed?: boolean
  applyUrl?: string
  secondaryActionText?: string
  secondaryButtonOnClickHandler?: () => void
  [key: string]: any // Allow dynamic properties
}

export interface ApiResponse<T> {
  data: T
  success: boolean
  message?: string
}

export interface TeamData {
  guid: string
  slug: string
  name: string
  [key: string]: any
}

export interface UserData {
  currentUser?: any
  currentTeam?: TeamData
  currentAgency?: any
}

export interface OnboardingData {
  teamGUID: string
  stepId: string
  completed: boolean
  [key: string]: any
}

export interface OnboardingResponse {
  user: {
    userName?: string | undefined
    organizationName?: string | undefined
    currentPlan?: string | undefined
    planPrice?: number | undefined
  }
  steps: OnboardingStep[]
  completedStepsCount: number
}

const QUICK_START_STEPS_CONFIG = [
  {
    id: ONBOARDING_STEP_IDS.INVITE_TEAM_MEMBERS,
    title: 'Invite your team members',
    description: 'Visit your team settings and invite your team members.',
    actionText: 'Invite Team',
    buttonOnClickHandler: () => {
      window.location.href = '/teams/settings/members'
    },
    showVideo: true,
    videoUrl: 'https://vimeo.com/744850460/7cf4b29f00',
    section: ONBOARDING_SECTIONS.QUICK_START,
  },
  {
    id: ONBOARDING_STEP_IDS.ADD_LOGO,
    title: 'Add your logo',
    description:
      "Send an email to help@snug.com, attach your Agency logo and we'll brand your team dashboard, property pages and communications.",
    actionText: 'Email help@snug.com',
    buttonOnClickHandler: () => {
      window.location.href = createMailtoUrl(
        ONBOARDING_EMAIL_TEMPLATES.LOGO_UPLOAD,
      )
    },
    showVideo: true,
    videoUrl: 'https://vimeo.com/744851108/91d8eaca12',
    section: ONBOARDING_SECTIONS.QUICK_START,
  },
  {
    id: ONBOARDING_STEP_IDS.APPLY_LINK,
    title: 'Apply Link',
    description:
      'Use your Apply Link everywhere: adverts, your website, stickers, QR Code for COVID Check in, filtered dates and Excluded properties as well as Entry Sheets.',
    actionText: 'Request customisation',
    buttonOnClickHandler: () => {
      widgetHelpers.openZendeskWidget()
    },
    showVideo: true,
    videoUrl: 'https://vimeo.com/744851491/debb81917d',
    section: ONBOARDING_SECTIONS.QUICK_START,
  },
  {
    id: ONBOARDING_STEP_IDS.TURN_ON_APPLICANT_SCREENING,
    title: 'Turn on applicant screening',
    description:
      'Activate the Equifax National Tenancy Database (NTD) or TCA here.',
    actionText: 'Activate Screening',
    buttonOnClickHandler: () => {
      window.location.href = '/settings/screening'
    },
    showVideo: false,
    section: ONBOARDING_SECTIONS.QUICK_START,
  },
]

export const fetchOnboardingData = async (
  teamGUID: string,
  userData: UserData = {},
): Promise<OnboardingResponse> => {
  if (!teamGUID) {
    throw new Error('Team GUID is required')
  }

  const { currentUser, currentTeam, currentAgency } = userData
  const userInfo = {
    userName: currentUser?.firstName || currentUser?.name,
    organizationName: currentTeam?.name,
    currentPlan: currentTeam?.subscription?.planName,
    planPrice: currentTeam?.subscription?.planPrice,
  }

  try {
    const response = await api.get(`/sm/team/${teamGUID}/onboarding/progress`)

    let teamPreferences = null
    if (currentAgency?.GUID) {
      try {
        const preferencesResponse = await fetchTeamPreferencesStatus(
          currentAgency.GUID,
        )
        teamPreferences = preferencesResponse.preferences || []
      } catch (prefsError) {}
    }

    const applyLinkPreference = teamPreferences?.find(
      (pref: any) =>
        pref.key === 'apply_link_url' ||
        pref.key === 'applyLinkUrl' ||
        pref.name?.toLowerCase().includes('apply link'),
    )
    const applyLinkUrl =
      applyLinkPreference?.value ||
      `https://snug.com/apply/${currentTeam?.slug || 'your-agency'}`

    const apiSteps = response.data || {}

    const quickStartSteps = QUICK_START_STEPS_CONFIG.map((stepConfig) => {
      const step = {
        ...stepConfig,
        completed: apiSteps[stepConfig.id]?.status === 'checked',
      } as OnboardingStep

      if (stepConfig.id === ONBOARDING_STEP_IDS.APPLY_LINK) {
        step.applyUrl = applyLinkUrl
      }

      if (stepConfig.id === ONBOARDING_STEP_IDS.INVITE_TEAM_MEMBERS) {
        step.buttonOnClickHandler = () => {
          const teamMembersUrl = urlTo(urlIds.teams.settings.members, {
            teamSlug: currentTeam?.slug,
          })
          window.location.href = teamMembersUrl
        }
      }

      if (stepConfig.id === ONBOARDING_STEP_IDS.TURN_ON_APPLICANT_SCREENING) {
        step.buttonOnClickHandler = () => {
          const screeningUrl = urlTo(urlIds.teams.settings.screening, {
            teamSlug: currentTeam?.slug,
          })
          window.location.href = screeningUrl
        }
      }

      return step
    })

    const setupSteps = [
      {
        id: ONBOARDING_STEP_IDS.CONNECT_PROPERTY_UPLOADER,
        title: 'Connect Property Uploader',
        description:
          'Select your property uploader to automatically sync listings and viewings',
        completed: apiSteps.connectPropertyUploader?.status === 'checked',
        actionText: 'Connect uploader',
        buttonOnClickHandler: () => {
          window.location.href = '/settings/integrations/property-uploader'
        },
        showVideo: true,
        videoUrl: 'https://vimeo.com/744852446/137e3a3145',
        section: ONBOARDING_SECTIONS.SETUP,
      },
      {
        id: ONBOARDING_STEP_IDS.SETUP_ENQUIRY_AUTORESPONDER,
        title: 'Setup the Enquiry AutoResponder',
        description: 'Set up your Auto Response and its Setup call.',
        completed: apiSteps.setUpTheEnquiryAutoResponder?.status === 'checked',
        actionText: 'Configure AutoResponder',
        buttonOnClickHandler: () => {
          window.location.href = '/settings/autoresponder'
        },
        showVideo: true,
        videoUrl: 'https://vimeo.com/744852933/afa98e3ccc',
        section: ONBOARDING_SECTIONS.TRAINING,
      },
      {
        id: ONBOARDING_STEP_IDS.CONNECT_UTILITY_PROVIDER,
        title: 'Connect Utility Provider',
        description: 'Set up Connection from the Outsourcing team.',
        completed: apiSteps.connectUtilityProvider?.status === 'checked',
        actionText: 'Configure',
        buttonOnClickHandler: () => {
          window.location.href = '/settings/integrations/utilities'
        },
        showVideo: true,
        videoUrl: 'https://vimeo.com/744852006/8b34327ef8',
        section: ONBOARDING_SECTIONS.SETUP,
      },
      {
        id: ONBOARDING_STEP_IDS.CONNECT_PROPERTY_MANAGEMENT_SYSTEM,
        title: 'Connect your Property Management System',
        description: "We'll help connect your Property Management Software.",
        completed:
          apiSteps.connectYourPropertyManagementSystem?.status === 'checked',
        actionText: 'Book a setup call',
        buttonOnClickHandler: () => {
          window.open(
            'https://snugcom.pipedrive.com/scheduler/49oqjuq/lets-get-you-set-up',
            '_blank',
          )
        },
        showVideo: true,
        videoUrl: 'https://vimeo.com/146325823',
        section: ONBOARDING_SECTIONS.SETUP,
      },
    ]

    const optimizeSteps = [
      {
        id: ONBOARDING_STEP_IDS.CUSTOMISE_APPLICATION_SETTINGS,
        title: 'Customise your Application Settings',
        description:
          'Applications + set your preferences to manage the number and quality of applications eg make people fill out the application that qualify',
        completed:
          apiSteps.customiseYourApplicationSettings?.status === 'checked',
        actionText: 'Configure Settings',
        buttonOnClickHandler: () => {
          window.location.href = `/teams/${currentTeam?.slug}/settings/applications`
        },
        showVideo: true,
        videoUrl: 'https://vimeo.com/1001401295',
        section: ONBOARDING_SECTIONS.OPTIMIZE,
      },
      {
        id: ONBOARDING_STEP_IDS.CUSTOMISE_VIEWINGS,
        title: 'Customise your viewings',
        description:
          'Set up your viewing preferences and booking settings to streamline the inspection process.',
        completed: apiSteps.customiseYourViewings?.status === 'checked',
        actionText: 'Configure Viewings',
        buttonOnClickHandler: () => {
          window.location.href = `/teams/${currentTeam?.slug}/settings/viewings`
        },
        showVideo: true,
        videoUrl: 'https://vimeo.com/1001401508',
        section: ONBOARDING_SECTIONS.OPTIMIZE,
      },
    ]

    const trainingSteps = [
      {
        id: ONBOARDING_STEP_IDS.BOOK_TRAINING,
        title: 'Live Group Training',
        description:
          'Training methods: 1 to 1 online training session at a mutually training session (60 to 90 mins per topic).',
        completed: apiSteps.bookTraining?.status === 'checked',
        actionText: 'Book Online Training',
        buttonOnClickHandler: () => {
          window.location.href = createMailtoUrl(
            ONBOARDING_EMAIL_TEMPLATES.BOOK_SETUP_CALL,
          )
        },
        showVideo: true,
        videoUrl: 'https://vimeo.com/745618542/7d8e12ff52',
        section: ONBOARDING_SECTIONS.TRAINING,
      },
    ]

    const legacySteps = [
      {
        id: ONBOARDING_STEP_IDS.TURN_OFF_LEGACY_SYSTEMS,
        title: 'Turn Off Legacy Systems',
        description:
          'Turn off Turn Off your current Application or respective existing system.',
        completed: apiSteps.turnOffLegacySystems?.status === 'checked',
        actionText: 'View Migration Guide',
        buttonOnClickHandler: () => {
          window.open(
            'https://help.snug.com/hc/en-us/articles/360001367715-Transitioning-your-Inspection-Bookings-to-Snug-Viewings-in-three-easy-steps',
            '_blank',
          )
        },
        // Additional button for Turn off Ignite
        secondaryActionText: 'Turn off Ignite',
        secondaryButtonOnClickHandler: () => {
          window.open(
            'https://help.snug.com/hc/en-us/articles/360000864096-How-to-disable-1form-on-realestate-com-au-REA-and-Domain-to-enable-Snug',
            '_blank',
          )
        },
        showVideo: false,
        section: ONBOARDING_SECTIONS.LEGACY,
      },
    ]

    const steps = [
      ...quickStartSteps,
      ...setupSteps,
      ...optimizeSteps,
      ...trainingSteps,
      ...legacySteps,
    ]

    const completedStepsCount = steps.filter((step) => step.completed).length

    return {
      user: userInfo,
      steps,
      completedStepsCount,
    }
  } catch (error) {
    const fallbackApplyUrl = `https://snug.com/apply/${
      userData.currentTeam?.slug || 'your-agency'
    }`

    const fallbackSteps = [
      ...QUICK_START_STEPS_CONFIG.map((step) => {
        const fallbackStep = {
          ...step,
          completed: step.id === ONBOARDING_STEP_IDS.INVITE_TEAM_MEMBERS,
        } as OnboardingStep

        if (step.id === ONBOARDING_STEP_IDS.APPLY_LINK) {
          fallbackStep.applyUrl = fallbackApplyUrl
        }

        if (step.id === ONBOARDING_STEP_IDS.INVITE_TEAM_MEMBERS) {
          fallbackStep.buttonOnClickHandler = () => {
            const teamMembersUrl = urlTo(urlIds.teams.settings.members, {
              teamSlug: userData.currentTeam?.slug,
            })
            window.location.href = teamMembersUrl
          }
        }

        if (step.id === ONBOARDING_STEP_IDS.TURN_ON_APPLICANT_SCREENING) {
          fallbackStep.buttonOnClickHandler = () => {
            const screeningUrl = urlTo(urlIds.teams.settings.screening, {
              teamSlug: userData.currentTeam?.slug,
            })
            window.location.href = screeningUrl
          }
        }

        return fallbackStep
      }),
      {
        id: ONBOARDING_STEP_IDS.CONNECT_PROPERTY_UPLOADER,
        title: 'Connect Property Uploader',
        description:
          'Select your property uploader to automatically sync listings and viewings',
        completed: false,
        actionText: 'Configure UI Source',
        buttonOnClickHandler: () => {
          window.location.href = '/settings/integrations/property-uploader'
        },
        showVideo: false,
        section: ONBOARDING_SECTIONS.SETUP,
      },
      {
        id: ONBOARDING_STEP_IDS.SETUP_ENQUIRY_AUTORESPONDER,
        title: 'Setup the Enquiry AutoResponder',
        description: 'Set up your Auto Response and its Setup call.',
        completed: false,
        actionText: 'Configure AutoResponder',
        buttonOnClickHandler: () => {
          window.location.href = '/settings/autoresponder'
        },
        showVideo: false,
        section: ONBOARDING_SECTIONS.TRAINING,
      },
      {
        id: ONBOARDING_STEP_IDS.CONNECT_UTILITY_PROVIDER,
        title: 'Connect Utility Provider',
        description: 'Set up Connection from the Outsourcing team.',
        completed: false,
        actionText: 'Configure',
        buttonOnClickHandler: () => {
          window.location.href = '/settings/integrations/utilities'
        },
        showVideo: false,
        section: ONBOARDING_SECTIONS.SETUP,
      },
      {
        id: ONBOARDING_STEP_IDS.CONNECT_PROPERTY_MANAGEMENT_SYSTEM,
        title: 'Connect your Property Management System',
        description: "We'll help connect your Property Management Software.",
        completed: false,
        actionText: 'Book a setup call',
        buttonOnClickHandler: () => {
          window.open(
            'https://snugcom.pipedrive.com/scheduler/49oqjuq/lets-get-you-set-up',
            '_blank',
          )
        },
        showVideo: true,
        videoUrl: 'https://vimeo.com/146325823',
        section: ONBOARDING_SECTIONS.SETUP,
      },
      {
        id: ONBOARDING_STEP_IDS.CUSTOMISE_APPLICATION_SETTINGS,
        title: 'Customise your Application Settings',
        description:
          'Applications + set your preferences to manage the number and quality of applications eg make people fill out the application that qualify.',
        completed: false,
        actionText: 'Configure Settings',
        buttonOnClickHandler: () => {
          window.location.href = `/teams/${userData.currentTeam?.slug}/settings/applications`
        },
        showVideo: false,
        section: ONBOARDING_SECTIONS.OPTIMIZE,
      },
      {
        id: ONBOARDING_STEP_IDS.CUSTOMISE_VIEWINGS,
        title: 'Customise your viewings',
        description:
          'Set up your viewing preferences and booking settings to streamline the inspection process.',
        completed: false,
        actionText: 'Configure Viewings',
        buttonOnClickHandler: () => {
          window.location.href = `/teams/${currentTeam?.slug}/settings/viewings`
        },
        showVideo: false,
        section: ONBOARDING_SECTIONS.OPTIMIZE,
      },
      {
        id: ONBOARDING_STEP_IDS.CONNECT_STAFFLINK,
        title: 'Connect Stafflink',
        description: 'Connect your staff management system.',
        completed: false,
        actionText: 'Connect Stafflink',
        buttonOnClickHandler: () => {
          window.location.href = '/settings/integrations/stafflink'
        },
        showVideo: false,
        section: ONBOARDING_SECTIONS.OPTIMIZE,
      },
      {
        id: ONBOARDING_STEP_IDS.LEASE_OFFER_CONGRATULATIONS_TEMPLATE,
        title: 'Lease Offer Congratulations Template',
        description:
          'Customize congratulations messages for successful applications.',
        completed: false,
        actionText: 'Configure Template',
        buttonOnClickHandler: () => {
          window.location.href = '/settings/templates/congratulations'
        },
        showVideo: false,
        section: ONBOARDING_SECTIONS.OPTIMIZE,
      },
      {
        id: ONBOARDING_STEP_IDS.BOOK_TRAINING,
        title: 'Live Group Training',
        description:
          'Training methods: 1 to 1 online training session at a mutually training session (60 to 90 mins per topic).',
        completed: false,
        actionText: 'Book Online Training',
        buttonOnClickHandler: () => {
          window.location.href = createMailtoUrl(
            ONBOARDING_EMAIL_TEMPLATES.BOOK_SETUP_CALL,
          )
        },
        showVideo: false,
        section: ONBOARDING_SECTIONS.TRAINING,
      },
      {
        id: ONBOARDING_STEP_IDS.TURN_OFF_LEGACY_SYSTEMS,
        title: 'Turn Off Legacy Systems',
        description:
          'Turn off Turn Off your current Application or respective existing system.',
        completed: false,
        actionText: 'View Migration Guide',
        buttonOnClickHandler: () => {
          window.open('https://help.snug.com/migration-guide', '_blank')
        },
        // Additional button for Turn off Ignite
        secondaryActionText: 'Turn off Ignite',
        secondaryButtonOnClickHandler: () => {
          window.open(
            'https://help.snug.com/hc/en-us/articles/360000864096-How-to-disable-1form-on-realestate-com-au-REA-and-Domain-to-enable-Snug',
            '_blank',
          )
        },
        showVideo: false,
        section: ONBOARDING_SECTIONS.LEGACY,
      },
    ]

    return {
      user: userInfo,
      steps: fallbackSteps,
      completedStepsCount: fallbackSteps.filter((step) => step.completed)
        .length,
    }
  }
}

export const updateStepCompletion = async (
  teamGUID: string,
  stepId: string,
  completed: boolean,
) => {
  if (!teamGUID) {
    snugNotifier.error('Team GUID is required')
  }

  if (!stepId) {
    snugNotifier.error('Step ID is required')
  }

  try {
    const response = await api.patch(`/sm/team/${teamGUID}/onboarding/items`, {
      key: stepId,
      action: completed ? 'checked' : 'unchecked',
    })

    return {
      success: true,
      stepId,
      completed,
      updatedAt: response.updatedAt || new Date().toISOString(),
    }
  } catch (error) {
    return {
      success: true,
      stepId,
      completed,
    }
  }
}

export const handleStepAction = async (stepId: string, actionType: string) => {
  return {
    success: true,
    stepId,
    actionType,
    message: `Action ${actionType} completed for step ${stepId}`,
  }
}

export const fetchTeamPreferencesStatus = async (agencyGUID: string) => {
  if (!agencyGUID) {
    snugNotifier.error('Agency GUID is required')
  }

  try {
    const response = await api.get(`sm/teams/${agencyGUID}/preferences/status`)

    return {
      preferences: response.data || [],
      meta: response.meta || null,
      errors: response.errors || null,
    }
  } catch (error) {
    const apiError = error as any
    return {
      preferences: [],
      meta: null,
      errors: snugNotifier.error(apiError?.message),
    }
  }
}
